import {
  faLongArrowLeft,
  faLongArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../../components/form/elements/select';
import { map } from 'rxjs/operators';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../../components/form';
import Button from '../../../../components/form/elements/button';
import File from '../../../../components/form/elements/file';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Decimal from '../../../../components/form/elements/decimal';
import Select from '../../../../components/form/elements/select';
import TextArea from '../../../../components/form/elements/text-area';
import Employee from '../../../../interfaces/employee';
import { AllEquipmentFormFields } from '../../../../interfaces/equipment';
import Paginate from '../../../../interfaces/paginate';
import { CROP_FIELDS_AND_WORKS_INDEX } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { numberTransform, route } from '../../../../services/utils';
import { FormData } from './create';
import { Section } from '../../../../pages/crop/fields-and-works';
import { useParams } from 'react-router-dom';

const schema = yup.object().shape({
  employees: yup.array().of(
    yup.object().shape({
      value: yup.string().required().uuid(),
    })
  ),
  duration: yup
    .number()
    .positive()
    .decimal()
    .nullable()
    .transform(numberTransform),
  equipment: yup.array().of(
    yup.object().shape({
      value: yup.string().required().uuid(),
    })
  ),
  notes: yup.string().nullable(),
  files: yup.array().of(
    yup.object().shape({
      id: yup.string().required().uuid(),
    })
  ),
});

export interface DetailsSubmitData {
  employees: (Option & { employee: Employee })[];
  duration: number;
  equipment: (Option & { equipment: AllEquipmentFormFields })[];
  notes: string;
  files: File[];
}

export default function StepDetails({
  formData,
  onReturn,
  onSubmit,
}: {
  formData: FormData;
  onReturn: () => void;
  onSubmit: SubmitType<DetailsSubmitData>;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const { employeeService, equipmentService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();

  const [body] = useState(() => [
    Group([
      Select('employees', {
        asyncOptions: (value, loadedOptions, { page }) => {
          return employeeService
            .index(farmId, {
              page: page,
              search: value,
            })
            .pipe(
              map((response: Paginate<Employee>) => ({
                hasMore: response.last_page > response.current_page,
                options: response.data.map((employee) => ({
                  label: employee.full_name,
                  value: employee.id,
                  employee: employee,
                })),
                additional: { page: response.current_page + 1 },
              }))
            );
        },
        label: tp('employees'),
        multi: true,
      }),
      Decimal('duration', tp('duration')),
      Select('equipment', {
        asyncOptions: (value, loadedOptions, { page }) => {
          return equipmentService
            .index(farmId, { page, equipment_type: null })
            .pipe(
              map((response: Paginate<AllEquipmentFormFields>) => ({
                hasMore: response.last_page > response.current_page,
                options: response.data.map((equipment) => ({
                  label: equipment.label,
                  value: equipment.id,
                  equipment: equipment,
                })),
                additional: { page: response.current_page + 1 },
              }))
            );
        },
        label: tp('equipment'),
        multi: true,
      }),
      TextArea('notes', tp('notes')),
      File('files', tp('files'), true),
    ]),
  ]);

  const [footer] = useState(() => [
    Button(
      'return',
      onReturn,
      'pages.crop.works.return',
      faLongArrowLeft,
      null,
      true
    ),
    Link(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      }),
      'cancel'
    ),
    Button('continue', null, 'pages.crop.works.continue', faLongArrowRight),
  ]);

  return (
    <div>
      <Form
        title={formData.id ? tp('edit') : tp('create')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema}
      />
    </div>
  );
}
